import { __decorate } from "tslib";
import { Group } from 'three';
import { LabelAngleConfig as Config } from '../../../Config/LabelAngleConfig.js';
import { SaveChild } from '../../../Core/Save.js';
import { Consts } from '../../../Utils/Consts.js';
import { FormatUtils } from '../../../Utils/FormatUtils.js';
import { Utils } from '../../../Utils/Utils.js';
import { VectorUtils } from '../../../Utils/VectorUtils.js';
import { Arc } from '../Arc.js';
import { DraggableLabel } from './DraggableLabel.js';
/**
 * Label that shows the angle between two points and draws an arc.
 */
export class LabelAngle extends Group {
    constructor(params) {
        super();
        this._needUpdateLabel = true;
        this._needUpdateArc = true;
        this._needUpdatePosition = true;
        this._scaleFactor = 1;
        this.onPositionComponentChangeEvent = () => {
            this._needUpdateLabel = true;
            this._needUpdateArc = true;
            this._needUpdatePosition = true;
        };
        this.name = params.name ?? 'LabelAngle';
        this.interceptedByRaycaster = true;
        this._params = params;
        params.textCallback = params.textCallback || ((angle) => `${angle}°`);
        params.invalidTextCallback = params.invalidTextCallback || params.textCallback;
        params.minAngle = params.minAngle ?? Number.MIN_SAFE_INTEGER;
        params.maxAngle = params.maxAngle ?? Number.MAX_SAFE_INTEGER;
        params.arcColor = params.arcColor ?? Config.color;
        params.bisectorDistanceToCenter = params.bisectorDistanceToCenter || 10; //todo add to conf
        this.add(this.arc = new Arc(params.name ?? 'LabelAngle_Arc', params.viewType, params.arcA.position, params.arcB.position, params.arcCenter.position, params.arcColor, params.arcDistanceToCenter), this.label = new DraggableLabel(params.name ?? 'LabelAngle_Label', params.viewType, params.arcCenter, params.labelAnchor, params.labelParams, params.labelScaleFactor, !params.placeOnBisector));
        this._validLabelParameters = Utils.clone(params.labelParams);
        this._invalidLabelParameters = params.labelErrorParams;
        this.arc.visible = false;
        if (params.placeOnBisector) {
            this._scaleFactor = Consts.scaleFactorLabel;
            this.updateAnchorBisector();
        }
        this.label.bindEvent(['onHover', 'onLeave'], () => {
            this.arc.visible = !this.arc.visible;
            if (this.arc.visible && this._needUpdateArc) {
                this._needUpdateArc = false;
                this.arc.update();
            }
        });
        params.arcA.bindEvent('onPositionComponentChange', this.onPositionComponentChangeEvent);
        params.arcB.bindEvent('onPositionComponentChange', this.onPositionComponentChangeEvent);
        params.arcCenter.bindEvent('onPositionComponentChange', this.onPositionComponentChangeEvent);
        params.arcA.addEventListener('historyChanged', this.onPositionComponentChangeEvent);
        params.arcB.addEventListener('historyChanged', this.onPositionComponentChangeEvent);
        params.arcCenter.addEventListener('historyChanged', this.onPositionComponentChangeEvent);
        this.addEventListener('historyChanged', () => this._needUpdateLabel = true);
        this.bindEvent("onFrameGeneration", () => {
            if (this._needUpdateLabel) {
                this._needUpdateLabel = false;
                const angle = FormatUtils.angleRadiants(params.arcA.position.clone().sub(params.arcCenter.position).angleTo(params.arcB.position.clone().sub(params.arcCenter.position)));
                const parameters = this._invalidLabelParameters && (angle > params.maxAngle || angle < params.minAngle) ? this._invalidLabelParameters : this._validLabelParameters;
                const textCallback = parameters === this._validLabelParameters ? params.textCallback : params.invalidTextCallback;
                parameters.text = textCallback(angle);
                this.label.setAllParams(parameters);
            }
            if (params.placeOnBisector && this._needUpdatePosition) {
                this.updateAnchorBisector();
                this._needUpdatePosition = false;
            }
        });
    }
    updateAnchorBisector() {
        const arcA = this._params.arcA.position;
        const arcB = this._params.arcB.position;
        const arcCenter = this._params.arcCenter.position;
        const dir = VectorUtils.getBisector(arcA.clone().sub(arcCenter), arcB.clone().sub(arcCenter));
        this.label.anchor = dir.setLength(this._params.bisectorDistanceToCenter * this._scaleFactor);
        this.label.defaultAnchor = this.label.anchor; //to fix reset
    }
    setValidParam(key, value) {
        this._validLabelParameters[key] = value;
        this._needUpdateLabel = true;
    }
    setInvalidParam(key, value) {
        this._invalidLabelParameters[key] = value;
        this._needUpdateLabel = true;
    }
    dispose() {
        super.dispose();
        this._params.arcA.unbindEvent('onPositionComponentChange', this.onPositionComponentChangeEvent);
        this._params.arcB.unbindEvent('onPositionComponentChange', this.onPositionComponentChangeEvent);
        this._params.arcCenter.unbindEvent('onPositionComponentChange', this.onPositionComponentChangeEvent);
        this._params.arcA.removeEventListener('historyChanged', this.onPositionComponentChangeEvent);
        this._params.arcB.removeEventListener('historyChanged', this.onPositionComponentChangeEvent);
        this._params.arcCenter.removeEventListener('historyChanged', this.onPositionComponentChangeEvent);
        this.label.dispose();
        this.arc.dispose();
    }
}
__decorate([
    SaveChild()
], LabelAngle.prototype, "label", void 0);
//# sourceMappingURL=LabelAngle.js.map